import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography, Alert, Divider, Link } from "@mui/material";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";

// In-app werkgever-handleiding (org-dashboard → tab "Handleiding").
// Bron: docs/manuals/werkgever-manual.md (v0.4) — dit component is de
// werkgever-gerichte weergave ervan: geen interne repo-verwijzingen, wel de
// screenshots. NL-primair (FR/EN volgen, net als de bronmanual). Wijzig je de
// werking van het model of het dashboard, werk dan bron én dit component bij.

const h2 = { fontSize: "1.25rem", mt: 4, mb: 1 } as const;
const h3 = { fontSize: "1.02rem", mt: 2.5, mb: 0.5 } as const;
const p = { mb: 1.5, color: "text.secondary", lineHeight: 1.7 } as const;

// Voordelen-overzicht bovenaan — zelfde eerlijke framing als de rest van het
// document: "ondersteunt de risicoanalyse" (niet: maakt compliant) en
// "gemodelleerde waarde" (niet: bewezen ROI).
const BENEFITS: { icon: React.ReactNode; title: string; text: React.ReactNode }[] = [
  {
    icon: <EuroRoundedIcon />,
    title: "Betaal enkel werkelijk gebruik",
    text: <>Geen prepaid pot of kost per aangesloten werknemer: maandfactuur op werkelijk sessieverbruik + één vaste jaarfee. De fair-use cap (standaard 6 sessies/jaar) begrenst uw maximale kost per medewerker.</>,
  },
  {
    icon: <GavelRoundedIcon />,
    title: "Input voor uw wettelijke verplichtingen",
    text: <>Gevalideerde instrumenten en anonieme barometer-aggregaten als input voor de verplichte psychosociale risicoanalyse (Welzijnswet, Codex Boek I, Titel 3) — in samenwerking met uw preventieadviseur en Comité PBW (§5).</>,
  },
  {
    icon: <ShieldRoundedIcon />,
    title: "Privacy by design (k≥10)",
    text: <>U ziet nooit individueel zorggebruik — technisch afgedwongen én contractueel verankerd. U verwerkt geen gezondheidsdata en hoeft geen verwerkersovereenkomst te sluiten (§6).</>,
  },
  {
    icon: <InsightsRoundedIcon />,
    title: "Aantoonbare waarde, eerlijk berekend",
    text: <>Het kwartaalrapport toont kosten vs ontvangen zorgwaarde en een vergelijking met klassieke benefits. De "waarde per bestede euro" is een gemodelleerde schatting met zichtbare aannames — geen ROI-belofte (§3.3).</>,
  },
  {
    icon: <FavoriteRoundedIcon />,
    title: "Drempelloos voor uw werknemers",
    text: <>Erkende psychologen zonder wachtlijst of verwijsbrief, gratis kennismaking, geen goedkeuringsstap bij HR — en boven de cap een naadloze overgang naar zelf betalen mét mutualiteits-terugbetaling (§1).</>,
  },
  {
    icon: <RocketLaunchRoundedIcon />,
    title: "Lancering in dagen, niet maanden",
    text: <>De Onboarding-kit-tab bevat kant-en-klare interne communicatie (automatisch ingevuld met uw gegevens), een CPBW-briefing en een DPO-one-pager (§2).</>,
  },
];

// "In 1 minuut": het hele model in zes simpele zinnen — toegevoegd 2026-07 na
// feedback dat de handleiding te moeilijk was. De volledige (juridisch
// precieze) uitleg blijft eronder staan; dit is de laagdrempelige ingang.
function OneMinuteCard() {
  const steps = [
    "Je werknemers praten online met een erkende psycholoog. Jij betaalt die gesprekken.",
    "Je betaalt alleen wat er echt gebruikt wordt: per gesprek een klein bedrag voor ons (€5 of €2) plus het tarief van de psycholoog, en één vast bedrag per jaar voor het platform.",
    "Elke werknemer kan standaard 6 gesprekken per jaar op jouw kosten doen. Daarna betaalt hij of zij zelf verder — dat gebeurt automatisch, jij merkt daar niets van.",
    "Jij ziet nooit wie een gesprek had. Je ziet alleen totalen, en alleen als minstens 10 mensen meedoen. Dat kan technisch niet anders én het staat in het contract.",
    "Met de barometer vraag je je hele team anoniem hoe het gaat. Ook daar zie je alleen groepsgemiddelden.",
    "Elk kwartaal krijg je een rapport dat je zo kunt doorsturen naar je directie of preventieadviseur — elke sectie legt zichzelf uit.",
  ];
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3, borderRadius: "var(--radius-lg)", border: "2px solid var(--primary-100)", bgcolor: "var(--surface)" }}>
      <Typography variant="h2" sx={{ fontSize: "1.2rem", mb: 1.5 }}>In één minuut: zo werkt het</Typography>
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {steps.map((st, i) => (
          <Typography key={i} component="li" sx={{ mb: 1, lineHeight: 1.7 }}>{st}</Typography>
        ))}
      </Box>
    </Box>
  );
}

// Woordenlijst: elk jargonwoord uit facturen/rapporten in gewone taal.
const GLOSSARY: [string, string][] = [
  ["Servicevergoeding", "Het bedrag dat Carefunded zelf aanrekent: €5 per therapiegesprek of €2 per coachinggesprek, plus de vaste jaarprijs."],
  ["Doorstorting zorgverleners", "Het deel van je factuur dat wij 1-op-1 doorbetalen aan de psychologen. Wij houden hier niets van; daarom staat er ook geen btw op."],
  ["Fair-use cap", "Het maximum aantal gesprekken per werknemer per jaar dat jij betaalt (standaard 6). Daarboven betaalt de werknemer automatisch zelf."],
  ["k≥10 (k-anonimiteit)", "Onze privacyregel: een cijfer wordt alleen getoond als het over minstens 10 mensen gaat. Bij minder zou je kunnen raden om wie het gaat."],
  ["Barometer-golf", "Eén meetronde van de anonieme teamvragenlijst. Jij kiest wanneer en welke onderdelen; deelname is vrijwillig."],
  ["Gemodelleerde schatting", "Een berekening op basis van aannames (bijvoorbeeld: één gesprek voorkomt gemiddeld een kwart verzuimdag) — een indicatie, geen meting bij jouw organisatie."],
  ["Adoptie", "Hoeveel van je werknemers het platform daadwerkelijk gebruiken."],
];

function GlossaryCard() {
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 4, borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", bgcolor: "var(--surface-alt, #fafafa)" }}>
      <Typography variant="h2" sx={{ fontSize: "1.2rem", mb: 1.5 }}>Woordenlijst: de termen op je factuur en in je rapport</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        {GLOSSARY.map(([term, expl], i) => (
          <Box key={i}>
            <Typography sx={{ fontWeight: 600, m: 0, fontSize: ".95rem" }}>{term}</Typography>
            <Typography sx={{ fontSize: ".9rem", color: "text.secondary", m: 0, lineHeight: 1.6 }}>{expl}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function BenefitsCard() {
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 4, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
      <Typography variant="h2" sx={{ fontSize: "1.2rem", mb: 2 }}>Waarom Carefunded voor uw organisatie</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
        {BENEFITS.map((b, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ color: "primary.dark", mt: 0.25, "& svg": { fontSize: 22 } }}>{b.icon}</Box>
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 0.25 }}>{b.title}</Typography>
              <Typography sx={{ fontSize: ".9rem", color: "text.secondary", lineHeight: 1.6, m: 0 }}>{b.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
      {items.map((it, i) => (
        <Typography key={i} component="li" sx={{ mb: 0.75, color: "text.secondary", lineHeight: 1.7 }}>{it}</Typography>
      ))}
    </Box>
  );
}

function Shot({ src, caption }: { src: string; caption: string }) {
  return (
    <Box component="figure" sx={{ m: 0, my: 2 }}>
      <Box component="img" src={src} alt={caption}
        sx={{ width: "100%", maxWidth: 860, display: "block", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }} />
      <Typography component="figcaption" sx={{ fontSize: ".8rem", color: "text.secondary", mt: 0.75 }}>{caption}</Typography>
    </Box>
  );
}

export default function WerkgeverManual() {
  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography sx={{ ...p, mb: 3 }}>
        Deze handleiding legt uit hoe het werkgevermodel werkt, wat u in dit dashboard kunt doen en welke
        privacygaranties daarbij gelden. Vragen? Neem contact op met uw Carefunded-contactpersoon.
      </Typography>

      <OneMinuteCard />

      <BenefitsCard />

      <GlossaryCard />

      {/* ---------- 1. Het model ---------- */}
      <Typography variant="h2" sx={h2}>1. Hoe het werkgevermodel werkt</Typography>
      <Typography sx={p}>
        Carefunded is een online platform voor geestelijke gezondheid. Uw werknemers worden gematcht met een
        erkende psycholoog of psychotherapeut voor coaching, therapie of begeleide zelfhulp, aangevuld met
        zelfhulp-Groeitrajecten en korte vragenlijsten.
      </Typography>

      <Typography variant="h3" sx={h3}>Wat u betaalt (usage-based)</Typography>
      <Bullets items={[
        <>U betaalt enkel voor de sessies die uw werknemers <strong>werkelijk gebruiken</strong> — geen vaste pot vooraf. Elke maand ontvangt u één transparante factuur.</>,
        <><strong>Per sessie:</strong> het sessietarief van de psycholoog + een kleine vaste servicevergoeding per sessie (<strong>€5</strong> therapie / <strong>€2</strong> coaching, excl. BTW).</>,
        <>Een <strong>vaste jaarlijkse servicevergoeding</strong> dekt het platform (dashboard, barometer-golven, rapportage, onboarding-support), los van het sessieverbruik. Tiers op organisatiegrootte, excl. BTW: <strong>€990</strong> (&lt;100 werknemers) / <strong>€1.990</strong> (100-500) / <strong>€4.990</strong> (&gt;500). Het actuele prijsblad ontvangt u bij uw offerte of via uw contactpersoon.</>,
        <>Een <strong>fair-use cap per werknemer per kalenderjaar</strong> (standaard <strong>6</strong> sessies, aanpasbaar) begrenst de kost. Bereikt een werknemer de cap, dan schakelt het platform <strong>automatisch</strong> over op zelf betalen door de werknemer (mét mutualiteits-terugbetaling) — <strong>u wordt daar nooit bij betrokken en ziet nooit wie het betreft</strong>. Er is bewust géén goedkeuringsstap bij HR.</>,
        <>Wil u méér dekken? Twee anonimiteits-veilige opties: de cap <strong>collectief verhogen</strong> (voor iedereen tegelijk), of een anonieme, <strong>gepoolde extra-sessie-pot</strong> financieren die wij naamloos toekennen — <strong>u ziet enkel "X van Y gebruikt"</strong>, nooit wie eruit putte.</>,
        <>Uw werknemer betaalt <strong>niets vooraf</strong> voor de door u gedekte sessies.</>,
      ]} />

      <Typography variant="h3" sx={h3}>Terugbetaling via de mutualiteit</Typography>
      <Typography sx={p}>
        Gaat een werknemer <strong>boven</strong> de fair-use cap en betaalt hij die sessies zelf, dan komt hij in
        aanmerking voor <strong>terugbetaling via zijn mutualiteit</strong> (aanvullende verzekering) — dit verlaagt
        zijn eigen bijdrage. Op de door u gedekte sessies is er geen terugbetaling (er is dan geen eigen kost).
        Carefunded <strong>toont</strong> de terugbetaling; ze wordt niet automatisch geregeld.
      </Typography>

      <Typography variant="h3" sx={h3}>Facturatie &amp; beheer</Typography>
      <Bullets items={[
        <><strong>Maandelijkse factuur</strong> met twee componenten: (a) onze <strong>eigen servicevergoedingen</strong> (€5/€2 per sessie, +21% BTW) en (b) de <strong>doorstorting van de sessietarieven</strong>, door ons geïnd "in naam en voor rekening van" de behandelende psychologen (zij zijn de zorgverstrekkers). De tarieven op de factuur zijn de tarieven zoals ze golden <strong>op het moment van boeking</strong>.</>,
        <>Op de factuur staan uitsluitend <strong>aantallen × type × maand</strong> — nooit datums, namen of andere details waaruit individueel zorggebruik af te leiden valt.</>,
        <><strong>Betalingstermijn: 30 dagen.</strong> Na de vervaldag ontvangt u één betalingsherinnering; blijft de factuur ook daarna onbetaald (14 dagen na de vervaldag), dan <strong>pauzeert de werkgeversfinanciering</strong>: uw werknemers vallen <strong>neutraal</strong> terug op zelf betalen (mét mutualiteits-terugbetaling) — zonder enige zichtbaarheid voor collega's en zonder vermelding van de reden. Na betaling wordt de financiering weer geactiveerd.</>,
        <><strong>Kostendrempel-alert (instelbaar):</strong> u ontvangt automatisch een melding zodra uw geaggregeerde kosten een door u ingestelde drempel bereiken — altijd totaalcijfers, nooit individueel verbruik.</>,
        <>U beheert dit alles in de tab <strong>Facturatie</strong>: maandverbruik, geschatte factuur, fair-use cap, kostendrempel, pool en de CSV-download van het jaaroverzicht (§3).</>,
      ]} />
      <Shot src="/manual/facturatie.png" caption="Tab Facturatie: maandverbruik en geschatte factuur, fair-use cap, kostendrempel-alert, sessiepool en het jaaroverzicht als CSV." />

      <Divider sx={{ my: 3 }} />

      {/* ---------- 2. Onboarding ---------- */}
      <Typography variant="h2" sx={h2}>2. Aan de slag: onboarding</Typography>
      <Bullets items={[
        <>Carefunded maakt uw organisatie aan met een unieke <strong>invite-code</strong> en een aantal <strong>seats</strong>.</>,
        <>Uw werknemers registreren zich en voeren de code in via <strong>Dashboard → Organisatie</strong> (veld "Uitnodigingscode" → knop <strong>Aansluiten</strong>). Ze worden lid — <strong>u ziet niet wie zich aansluit</strong>.</>,
        <>U logt in als org-admin (met verplichte tweestapsverificatie/MFA) en beheert alles vanuit dit dashboard.</>,
      ]} />
      <Shot src="/manual/dashboard-organisatie.png" caption="Uw dashboard, tab Organisatie: ledenaantal, dekking en de invite-code die u intern verspreidt." />
      <Shot src="/manual/werknemer-aansluiten.png" caption="Wat uw werknemer ziet: aansluiten bij de organisatie met de invite-code — u ziet nooit wie dat doet." />

      <Divider sx={{ my: 3 }} />

      {/* ---------- 3. Dashboard ---------- */}
      <Typography variant="h2" sx={h2}>3. Het dashboard gebruiken</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Privacybelofte:</strong> u ziet nooit individuele werknemers of individuele gezondheidsdata — alleen
        geaggregeerde, anonieme cijfers. Groepen kleiner dan <strong>10</strong> worden verborgen met de melding
        "Onvoldoende antwoorden om te tonen".
      </Alert>

      <Typography variant="h3" sx={h3}>3.1 Verbruik en kosten</Typography>
      <Typography sx={p}>
        Totale aantallen gefinancierde sessies per <strong>maand × type</strong> (therapie/coaching) — dezelfde
        granulariteit als op uw factuur, zonder datums of namen — plus kostentotalen en het verbruik van een
        eventuele gepoolde extra-sessie-pot ("pool: X/Y gebruikt"). Alerts bestaan uitsluitend op geaggregeerde
        kostendrempels, nooit op individueel gebruik.
      </Typography>

      <Typography variant="h3" sx={h3}>3.2 De anonieme organisatie-barometer</Typography>
      <Typography sx={p}>
        Periodiek stuurt u — via uw eigen interne communicatie; kant-en-klare teksten vindt u in de tab
        <strong> Onboarding-kit</strong>, automatisch ingevuld met uw gegevens — <strong>één gedeelde, anonieme
        link</strong> naar uw volledige personeelsbestand.
        Werknemers vullen in ±3 minuten een korte vragenlijst in; u kiest per meting welke onderdelen meegaan —
        welzijn (Cantril-ladder), burn-outsignalen (Burnout Assessment Tool) en/of werkbeleving (eigen
        Carefunded-vragen), zie §4.
      </Typography>
      <Typography sx={{ ...p, mb: 0.5 }}>Zo werkt de anonimiteit (door ontwerp, niet door belofte):</Typography>
      <Bullets items={[
        <><strong>Geen login, geen persoonlijke links:</strong> iedereen gebruikt dezelfde URL; er is geen koppeling met accounts.</>,
        <>Er wordt <strong>geen naam, userId, IP-adres of fijnmazig tijdstip</strong> opgeslagen (enkel de dag).</>,
        <>De deelnemer krijgt <strong>geen persoonlijke score</strong> — enkel een neutrale wegwijzer naar het hulpaanbod.</>,
        <>U ziet per golf uitsluitend <strong>groepsgemiddelden vanaf 10 respondenten</strong>. Daaronder wordt niets getoond, ook niet het aantal deelnemers. Er zijn geen team- of afdelingsuitsplitsingen.</>,
      ]} />
      <Typography sx={p}>
        <strong>Van inzicht naar actie:</strong> neem de golfresultaten mee naar uw preventieadviseur en Comité PBW
        als input voor concrete preventiemaatregelen (zie §5 voor het wettelijke kader).
      </Typography>
      <Shot src="/manual/barometer-golfrapport.png" caption="Golfrapport (k≥10) met vergelijking t.o.v. de vorige golf en de exportknoppen (PDF/CSV)." />

      <Typography variant="h3" sx={h3}>3.3 Rapporten exporteren</Typography>
      <Typography sx={{ ...p, mb: 0.5 }}>Drie exports, allemaal met dezelfde k≥10-discipline als het dashboard — ook in CSV en op facturen:</Typography>
      <Bullets items={[
        <><strong>Golfrapport</strong> (tab Welzijnsbarometer → "Bekijk rapport"): per barometer-golf afdrukken/bewaren als PDF of downloaden als CSV, telkens met de instrument-bronvermeldingen. Een golf (of module) onder de k-drempel is niet exporteerbaar — er lekt ook geen aantal.</>,
        <><strong>Jaaroverzicht</strong> (tab Facturatie → "Jaaroverzicht downloaden (CSV)"): aantallen × type × maand + bedragen — exact de factuurgranulariteit (bedragen zoals geboekt, zoals op de factuur), nooit datums of namen.</>,
        <><strong>Kwartaalrapport</strong> (eigen tab): het overzicht dat u elk kwartaal deelt met directie of preventieadviseur. Vijf genummerde secties die zichzelf uitleggen: (1) hoeveel gesprekken er waren, (2) hoeveel medewerkers meedoen, (3) hoe het team zich voelt — inclusief de vrijwillige uitkomstmeting (ervaren voortgang en nut, alleen groepsgemiddelden vanaf 10 deelnemers), (4) wat het kostte en wat u ervoor terugkreeg — met een rekenvoorbeeld in gewone taal — en (5) de vergelijking met andere personeelsvoordelen. Eerlijkheid voorop: wat medewerkers aan zorg kregen is een echt bedrag (de sessietarieven); het vermeden verzuim is een <em>schatting op basis van aannames</em>, en die aannames staan er gewoon bij zodat u ze met uw preventieadviseur kunt bijstellen. Afdrukken of bewaren als PDF kan met één knop.</>,
      ]} />
      <Shot src="/manual/kwartaalrapport.png" caption="Kwartaalrapport: de kerncijfers in één oogopslag en de genummerde secties die zichzelf uitleggen." />
      <Shot src="/manual/kwartaalrapport-impact.png" caption={'Sectie 4 van het rapport: de kosten regel per regel uitgelegd, met het "Zo lees je dit"-rekenvoorbeeld en de zichtbare aannames.'} />

      <Typography variant="h3" sx={h3}>3.4 Later, bij aangetoonde behoefte</Typography>
      <Typography sx={p}>
        Een uitgebreider analyse-dashboard (KPI-scores, driveranalyse, trends over golven heen) staat op de roadmap
        en zou uitsluitend op de anonieme barometer-golven bouwen — met dezelfde k≥10-suppressie. Ook
        wachttijd-afspraken en workshops staan bewust nog niet in het aanbod.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* ---------- 4. Instrumenten ---------- */}
      <Typography variant="h2" sx={h2}>4. De meetinstrumenten</Typography>
      <Typography sx={p}>
        Belangrijk voor uw DPO, preventieadviseur en Comité PBW: wij gebruiken een bewust beperkte set — twee
        wetenschappelijk gevalideerde instrumenten plus één transparant gelabelde set eigen werkbelevingsvragen.
        U kiest per meting welke onderdelen meegaan (standaard: alle drie).
      </Typography>
      <Bullets items={[
        <><strong>Cantril-ladder — welzijn</strong> (Cantril, 1965; o.a. gebruikt door de Gallup World Poll en het World Happiness Report): twee vragen (0–10) over hoe medewerkers hun leven nu en over vijf jaar inschatten; internationaal gevalideerd en vrij te gebruiken (publiek domein).</>,
        <><strong>BAT-12 — Burnout Assessment Tool</strong> (KU Leuven; Schaufeli, Desart &amp; De Witte): meet burn-outrisico/uitputting, met Belgische normen; vrij te gebruiken mits correcte citatie — die vermelden we in elk rapport en elke export.</>,
        <><strong>Carefunded-werkitems — werkbeleving</strong>: zeven eigen vragen over werkdruk, energie, steun van leidinggevende en collega's, werk-privébalans, autonomie en meldveiligheid. Dit zijn eigen vragen — <strong>géén gevalideerd klinisch instrument en géén klinische screening</strong>; we labelen dat ook zo in de vragenlijst en in het rapport.</>,
        <><strong>Bewust níét gebruikt:</strong> WHO-5 (non-commercial-licentie sinds de copyright-overdracht aan de WHO; in juli 2026 vervangen door de Cantril-ladder), COPSOQ III en UWES (licenties laten commercieel gebruik niet toe zonder aparte overeenkomst) en klinische screeners zoals PHQ-9/GAD-7 — die impliceren individuele ernst-feedback en dat doen we niet: deelnemers krijgen een uniforme wegwijzer, geen score of label.</>,
        <>Alle resultaten zijn <strong>uitsluitend anonieme groepsgemiddelden (k≥10)</strong> — nooit een werkgever-zichtbare individuele score.</>,
      ]} />

      <Divider sx={{ my: 3 }} />

      {/* ---------- 5. Wettelijk kader ---------- */}
      <Typography variant="h2" sx={h2}>5. Hoe dit uw wettelijke verplichtingen ondersteunt</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Dit ondersteunt uw verplichtingen — het vervangt de rol van uw preventieadviseur niet, maar levert de
        waardevolle input die hij nodig heeft.
      </Alert>
      <Bullets items={[
        <>Als werkgever bent u wettelijk verplicht een <strong>psychosociale risicoanalyse a priori</strong> uit te voeren (Welzijnswet 4 augustus 1996, hfdst. Vbis; Codex over het welzijn op het werk, Boek I, Titel 3). Die analyse moet vijf domeinen bekijken: arbeidsorganisatie, arbeidsinhoud, arbeidsvoorwaarden, arbeidsomstandigheden en interpersoonlijke relaties — precies de dimensies die dit dashboard meet.</>,
        <>Carefunded levert <strong>input voor uw preventieadviseur/externe dienst</strong>: gevalideerde instrumenten (Cantril-ladder/BAT-12), aangevuld met eigen werkbelevingsvragen, en anonieme barometer-aggregaten die u kunt voorleggen aan uw preventieadviseur psychosociale aspecten (PAPA) en Comité PBW. Let wel: deelname aan de barometer is vrijwillig (zelfselectie), dus de resultaten zijn een signaal — geen vervanging van het erkende risicoanalyse-instrumentarium van uw externe dienst.</>,
        <><strong>Grenzen die we duidelijk communiceren:</strong> u blijft wettelijk verantwoordelijk; de PAPA moet betrokken worden (advies vóór maatregelen); het Comité PBW en werknemersinspraak blijven verplicht; en er moeten effectief preventiemaatregelen volgen op de analyse.</>,
      ]} />

      <Divider sx={{ my: 3 }} />

      {/* ---------- 6. Privacy ---------- */}
      <Typography variant="h2" sx={h2}>6. Privacy &amp; anonimiteit</Typography>
      <Bullets items={[
        <>U ontvangt <strong>uitsluitend anonieme aggregaten</strong>. Carefunded is verwerkingsverantwoordelijke voor de ruwe gegevens; u als werkgever verwerkt géén individuele gezondheidsdata.</>,
        <><strong>Minimale groepsgrootte = 10</strong>, platformbreed en gepubliceerd op onze <Link component={RouterLink} to="/privacystandaard">privacystandaard</Link>. Onder de 10 tonen we níéts — ook geen aantallen.</>,
        <>We voorkomen <strong>complementaire onthulling</strong>: rapporten bestaan enkel per vaste golf en per vast kwartaal, zonder vrije filters of tijdssnedes — er zijn dus geen views die u van elkaar kunt aftrekken om een kleine groep te reconstrueren.</>,
        <>Individuele antwoorden zijn <strong>alleen voor de werknemer</strong> zichtbaar, met een neutrale doorverwijzing naar hulp.</>,
        <>Alles conform GDPR (bijzondere categorie — art. 9) en onze DPIA. Zie ook onze <Link component={RouterLink} to="/sub-verwerkers">sub-verwerkerslijst</Link> en <Link component={RouterLink} to="/privacy">privacyverklaring</Link>.</>,
      ]} />
    </Box>
  );
}
